import { Link } from 'react-router-dom';
import { ArrowRight, FileText, BookOpen } from 'lucide-react';

const pages = [
  {
    number: 1,
    title: 'Cover Page',
    type: 'content',
    description: 'Introduction to the Open Questions Framework'
  },
  {
    number: 2,
    title: 'Welcome & How to Use This Workbook',
    type: 'content',
    description: 'Overview and instructions'
  },
  {
    number: 3,
    title: 'Your Learning Journey',
    type: 'content',
    description: 'Visual summary map'
  },
  {
    number: 4,
    title: 'Curriculum Summary',
    type: 'content',
    description: 'Table of contents with learning objectives'
  },
  {
    number: 5,
    title: 'Module 1 Title Page',
    type: 'content',
    description: 'Module introduction'
  },
  {
    number: 6,
    title: 'Core Concept & Tool',
    type: 'content',
    description: 'High-Leverage Unknown and 3-Point Checklist'
  },
  {
    number: 7,
    title: 'Reference: "Bad" vs. "Good" Examples',
    type: 'content',
    description: 'Question transformation examples'
  },
  {
    number: 8,
    title: 'Activity: Scenario A, "The Pro Tier Push"',
    type: 'activity',
    description: 'Worksheet 1.1 - Identify bad questions',
    route: '/elevation/workbook/oqf/module-1/worksheet-1-1'
  },
  {
    number: 9,
    title: 'Activity: Frame the High-Leverage Unknown',
    type: 'activity',
    description: 'Worksheet 1.2 - Rewrite questions',
    route: '/elevation/workbook/oqf/module-1/worksheet-1-2'
  },
  {
    number: 10,
    title: 'Module 1 Notes',
    type: 'content',
    description: 'Blank notes page'
  }
];

export default function Module1Page() {
  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--slate-dim)] border-b border-[var(--slate)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link
            to="/elevation/workbook/oqf"
            className="text-[var(--text-sec)] hover:text-[var(--text-pri)] text-sm mb-4 inline-block"
          >
            ← Back to OQF Home
          </Link>
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">
            Module 1: Diagnose & Frame High-Leverage Unknowns
          </h1>
          <p className="text-[var(--text-sec)] mt-2">
            Objective: By the end of this module, you will be able to rewrite vague, bundled, or biased questions into precise, high-leverage unknowns.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            💡 Core Concept: The "High-Leverage Unknown"
          </h2>
          <div className="space-y-3 text-[var(--text-sec)]">
            <p>
              Not all questions are created equal. We waste a lot of time on questions that don't actually matter. The OQF helps you focus on the ones that do.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="bg-[var(--bg-raised)] p-4 rounded border border-[var(--slate)]">
                <p className="font-semibold text-[var(--text-pri)] mb-2">Low-Leverage Unknown</p>
                <p className="text-sm">An interesting question, but knowing the answer <strong>would not</strong> materially change your decision or outcome.</p>
              </div>
              <div className="bg-[var(--bg-raised)] p-4 rounded border border-[var(--slate)]">
                <p className="font-semibold text-[var(--text-pri)] mb-2">High-Leverage Unknown</p>
                <p className="text-sm">A question whose answer <strong>would change what you do next.</strong> It's an uncertainty that, if clarified, would meaningfully shift your priorities, choices, or confidence.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            🛠️ Tool: The 3-Point OQF Question Checklist
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            A question is "OQF-Ready" only if it meets all three criteria.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--surface)]">
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">Criterion</th>
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">What it Means</th>
                  <th className="px-4 py-3 text-left font-semibold text-[var(--text-pri)]">Quick Test</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--rule)]">
                <tr>
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">1. Singular Unknown</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">The question isolates <strong>one specific uncertainty</strong> instead of bundling multiple issues.</td>
                  <td className="px-4 py-3 text-[var(--text-sec)] italic">Ask: Could this be answered in one sentence? If not, split it.</td>
                </tr>
                <tr className="bg-[var(--surface)]">
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">2. Decision-Linked</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">The answer would <strong>unlock or change a concrete decision, action, or priority.</strong></td>
                  <td className="px-4 py-3 text-[var(--text-sec)] italic">Ask: If we knew this, what would we do differently tomorrow?</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium text-[var(--text-pri)]">3. Neutral & Investigable</td>
                  <td className="px-4 py-3 text-[var(--text-sec)]">The question is <strong>free of bias, judgment, or implied solutions</strong> and can be answered through evidence.</td>
                  <td className="px-4 py-3 text-[var(--text-sec)] italic">Ask: Does this invite discovery—or argue a point?</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-6">Module Pages</h2>
          <div className="space-y-2">
            {pages.map((page) => (
              <div
                key={page.number}
                className="flex items-center justify-between p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-[var(--slate)] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full font-semibold text-sm">
                    {page.number}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[var(--text-pri)]">{page.title}</h3>
                    <p className="text-sm text-[var(--text-sec)]">{page.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {page.type === 'activity' ? (
                    <>
                      <FileText className="w-5 h-5 text-[var(--text-ter)]" />
                      <Link
                        to={page.route!}
                        className="text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium flex items-center gap-1"
                      >
                        Open Activity
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </>
                  ) : (
                    <BookOpen className="w-5 h-5 text-[var(--text-ter)]" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <Link
            to="/elevation/workbook/oqf"
            className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            ← Back to Home
          </Link>
          <Link
            to="/elevation/workbook/oqf/module-1/worksheet-1-1"
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] flex items-center gap-2"
          >
            Start Activity
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






